import { AxiosResponse } from 'axios';

import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType, TObject } from 'utils/types';

import { dictionaryInstance } from '../base';

import {
  normalizeBulkCreateResultCodes,
  normalizeBulkUpdateResultCodes,
  normalizeResultCode,
  normalizeResultCodesStates,
} from './normalizers';
import {
  IBulkCreateResultCodesPayload,
  IBulkCreateResultCodesResult,
  IBulkUpdateResultCodesPayload,
  IBulkUpdateResultCodesResult,
  IResultCode,
  IResultCodesForm,
  IResultCodeStates,
} from './types';

const resultCodesAPI = {
  async getAllResultCodes(filters: IAllPageFilters): Promise<IPageType<IResultCode>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await dictionaryInstance.get('result-code', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, IResultCode>(
      data,
      normalizeResultCode,
      JSON.stringify(filters.search),
    );
  },

  async createResultCode(payload: IResultCodesForm): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await dictionaryInstance.post(`result-code`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editResultCode({ id, ...payload }: IResultCodesForm): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await dictionaryInstance.put(`result-code/${id}`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async deleteResultCode(id?: number): Promise<AxiosResponse> {
    const result = await dictionaryInstance.delete(`result-code/${id}`);

    return result;
  },

  async bulkCreateResultCodes(
    payload: IBulkCreateResultCodesPayload,
  ): Promise<IBulkCreateResultCodesResult> {
    const normalizedPayload = prepareFormData(payload);

    const response = await dictionaryInstance.post('result-code/bulk', normalizedPayload);

    return normalizeBulkCreateResultCodes(response?.data);
  },

  async bulkUpdateResultCodes({
    clearFields,
    filters,
    patch,
  }: IBulkUpdateResultCodesPayload): Promise<IBulkUpdateResultCodesResult> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await dictionaryInstance.post('result-code/bulk-update', {
      clearFields,
      patch: prepareFormData(patch),
      search,
    });

    return normalizeBulkUpdateResultCodes(response?.data);
  },

  async getResultCodeStates(code?: string): Promise<IResultCodeStates> {
    const response = await dictionaryInstance.get(`result-code/states`, {
      params: { resultCode: code },
    });

    return normalizeResultCodesStates(response?.data);
  },
};

export default resultCodesAPI;
